import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import MetricCard from './components/MetricCard';
import UserJourneyFunnel from './components/UserJourneyFunnel';
import FeatureHeatmap from './components/FeatureHeatmap';
import SpendingPatternChart from './components/SpendingPatternChart';
import TopFeaturesRanking from './components/TopFeaturesRanking';
import SentimentAnalysis from './components/SentimentAnalysis';
import UserBehaviorTable from './components/UserBehaviorTable';
import FilterPanel from './components/FilterPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const UserAnalyticsHub = () => {
  const [activeTab, setActiveTab] = useState('funnel');
  const [chartType, setChartType] = useState('bar');
  const [filters, setFilters] = useState({
    segment: 'all',
    dateRange: '7d',
    regions: [],
    comparisonMode: false
  });

  const metricsData = [
    {
      title: 'New User Acquisition',
      value: '2,847',
      change: '+18.2%',
      changeType: 'positive',
      icon: 'UserPlus',
      trend: [20, 25, 30, 28, 35, 40, 45],
      significance: 'High',
      description: 'Monthly new signups'
    },
    {
      title: 'Pro Tier Conversion',
      value: '12.4%',
      change: '+2.1%',
      changeType: 'positive',
      icon: 'TrendingUp',
      trend: [10, 11, 10.5, 11.5, 12, 12.2, 12.4],
      significance: 'Medium',
      description: 'Free to Pro upgrade rate'
    },
    {
      title: 'Feature Adoption',
      value: '68.3%',
      change: '+5.7%',
      changeType: 'positive',
      icon: 'Zap',
      trend: [60, 62, 64, 65, 66, 67, 68.3],
      description: 'Active feature usage'
    },
    {
      title: 'User Retention',
      value: '84.6%',
      change: '-1.2%',
      changeType: 'negative',
      icon: 'Users',
      trend: [86, 85.5, 85, 85.2, 84.8, 84.5, 84.6],
      description: '30-day retention cohort'
    }
  ];

  const funnelData = [
    {
      stage: 'Sign Up',
      users: 10000,
      avgTime: '0 min',
      icon: 'UserPlus'
    },
    {
      stage: 'Email Verification',
      users: 8500,
      avgTime: '2 min',
      icon: 'Mail'
    },
    {
      stage: 'Wallet Connection',
      users: 7200,
      avgTime: '5 min',
      icon: 'Wallet'
    },
    {
      stage: 'First Transaction',
      users: 5800,
      avgTime: '1.2 days',
      icon: 'ArrowRightLeft'
    },
    {
      stage: 'Trial Activation',
      users: 4200,
      avgTime: '2.5 days',
      icon: 'Play'
    },
    {
      stage: 'Pro Conversion',
      users: 1240,
      avgTime: '4.2 days',
      icon: 'Crown'
    }
  ];

  const heatmapData = [
    {
      category: 'Payment Features',
      icon: 'CreditCard',
      features: [
        { name: 'Swap Engine', usage: 92, users: 8234, frequency: 4.2, satisfaction: 4.7, description: 'Crypto-to-crypto and crypto-to-fiat swaps with real-time rates' },
        { name: 'Card Payments', usage: 78, users: 6982, frequency: 3.8, satisfaction: 4.5, description: 'Credit/debit card integration for fiat purchases' },
        { name: 'Auto-Convert', usage: 65, users: 5821, frequency: 2.1, satisfaction: 4.3, description: 'Automated balance conversion based on rules' },
        { name: 'Multi-Rail', usage: 54, users: 4832, frequency: 1.9, satisfaction: 4.2, description: 'BTC, ETH, and stablecoin payment routing' },
        { name: 'Batch Payments', usage: 42, users: 3761, frequency: 1.2, satisfaction: 4.1, description: 'Multiple recipient payment processing' },
        { name: 'Recurring Bills', usage: 38, users: 3402, frequency: 0.8, satisfaction: 4.0, description: 'Automated subscription and bill payments' }
      ]
    },
    {
      category: 'Portfolio Management',
      icon: 'PieChart',
      features: [
        { name: 'Balance View', usage: 95, users: 8503, frequency: 8.5, satisfaction: 4.8, description: 'Real-time multi-currency balance tracking' },
        { name: 'Analytics', usage: 71, users: 6358, frequency: 2.4, satisfaction: 4.4, description: 'Transaction history and spending insights' },
        { name: 'Alerts', usage: 68, users: 6086, frequency: 3.2, satisfaction: 4.6, description: 'Balance threshold and price movement notifications' },
        { name: 'Tax Reports', usage: 45, users: 4029, frequency: 0.3, satisfaction: 4.2, description: 'Automated tax document generation' },
        { name: 'Export Data', usage: 39, users: 3491, frequency: 0.5, satisfaction: 4.1, description: 'CSV and PDF transaction exports' },
        { name: 'Budget Tools', usage: 33, users: 2954, frequency: 1.1, satisfaction: 3.9, description: 'Spending limits and budget tracking' }
      ]
    },
    {
      category: 'Security & Settings',
      icon: 'Shield',
      features: [
        { name: '2FA Setup', usage: 88, users: 7878, frequency: 0.1, satisfaction: 4.9, description: 'Two-factor authentication configuration' },
        { name: 'Wallet Mgmt', usage: 76, users: 6803, frequency: 1.3, satisfaction: 4.5, description: 'Multi-wallet connection and management' },
        { name: 'KYC Verify', usage: 69, users: 6177, frequency: 0.1, satisfaction: 4.3, description: 'Identity verification for compliance' },
        { name: 'Notifications', usage: 82, users: 7340, frequency: 4.7, satisfaction: 4.4, description: 'Customizable alert preferences' },
        { name: 'API Keys', usage: 28, users: 2507, frequency: 0.2, satisfaction: 4.6, description: 'Developer API access management' },
        { name: 'Privacy', usage: 51, users: 4565, frequency: 0.4, satisfaction: 4.7, description: 'Data sharing and privacy controls' }
      ]
    },
    {
      category: 'Pro Features',
      icon: 'Crown',
      features: [
        { name: 'Advanced Rules', usage: 73, users: 2190, frequency: 2.8, satisfaction: 4.8, description: 'Complex automation rule configuration' },
        { name: 'Priority Support', usage: 81, users: 2430, frequency: 0.6, satisfaction: 4.9, description: 'Dedicated customer support access' },
        { name: 'Lower Fees', usage: 94, users: 2820, frequency: 5.2, satisfaction: 4.9, description: 'Reduced transaction and swap fees' },
        { name: 'API Access', usage: 42, users: 1260, frequency: 3.1, satisfaction: 4.7, description: 'Full API integration capabilities' },
        { name: 'White Label', usage: 18, users: 540, frequency: 0.3, satisfaction: 4.5, description: 'Custom branding for merchants' },
        { name: 'Analytics Pro', usage: 67, users: 2010, frequency: 4.5, satisfaction: 4.6, description: 'Advanced business intelligence tools' }
      ]
    }
  ];

  const spendingData = [
    { month: 'Jul', proUsers: 142000, freeUsers: 28000 },
    { month: 'Aug', proUsers: 158000, freeUsers: 31000 },
    { month: 'Sep', proUsers: 171000, freeUsers: 34000 },
    { month: 'Oct', proUsers: 189000, freeUsers: 38000 },
    { month: 'Nov', proUsers: 205000, freeUsers: 42000 },
    { month: 'Dec', proUsers: 234000, freeUsers: 47000 }
  ];

  const topFeatures = [
    {
      name: 'Swap Engine',
      users: 8234,
      engagement: 92,
      trend: 'up',
      change: '+8.2%',
      category: 'Payments'
    },
    {
      name: 'Balance View',
      users: 8503,
      engagement: 95,
      trend: 'up',
      change: '+5.1%',
      category: 'Portfolio'
    },
    {
      name: '2FA Security',
      users: 7878,
      engagement: 88,
      trend: 'up',
      change: '+12.3%',
      category: 'Security'
    },
    {
      name: 'Card Payments',
      users: 6982,
      engagement: 78,
      trend: 'up',
      change: '+6.7%',
      category: 'Payments'
    },
    {
      name: 'Notifications',
      users: 7340,
      engagement: 82,
      trend: 'stable',
      change: '+0.8%',
      category: 'Settings'
    },
    {
      name: 'Wallet Management',
      users: 6803,
      engagement: 76,
      trend: 'down',
      change: '-2.1%',
      category: 'Security'
    }
  ];

  const sentimentData = [
    { sentiment: 'positive', count: 4823 },
    { sentiment: 'neutral', count: 1247 },
    { sentiment: 'negative', count: 382 }
  ];

  const userData = [
    {
      id: 1,
      name: 'Sarah Chen',
      email: 'sarah.chen@example.com',
      tier: 'Pro',
      engagement: 94,
      transactions: 342,
      totalSpend: 12847,
      lastActive: '2 hours ago'
    },
    {
      id: 2,
      name: 'Marcus Johnson',
      email: 'marcus.j@example.com',
      tier: 'Free',
      engagement: 67,
      transactions: 89,
      totalSpend: 2341,
      lastActive: '5 hours ago'
    },
    {
      id: 3,
      name: 'Elena Rodriguez',
      email: 'elena.r@example.com',
      tier: 'Pro',
      engagement: 88,
      transactions: 267,
      totalSpend: 9823,
      lastActive: '1 day ago'
    },
    {
      id: 4,
      name: 'David Kim',
      email: 'david.kim@example.com',
      tier: 'Pro',
      engagement: 91,
      transactions: 412,
      totalSpend: 15234,
      lastActive: '3 hours ago'
    },
    {
      id: 5,
      name: 'Aisha Patel',
      email: 'aisha.p@example.com',
      tier: 'Free',
      engagement: 54,
      transactions: 67,
      totalSpend: 1876,
      lastActive: '2 days ago'
    },
    {
      id: 6,
      name: 'James Wilson',
      email: 'james.w@example.com',
      tier: 'Pro',
      engagement: 82,
      transactions: 198,
      totalSpend: 7654,
      lastActive: '6 hours ago'
    },
    {
      id: 7,
      name: 'Yuki Tanaka',
      email: 'yuki.t@example.com',
      tier: 'Free',
      engagement: 71,
      transactions: 124,
      totalSpend: 3421,
      lastActive: '1 day ago'
    },
    {
      id: 8,
      name: 'Mohammed Al-Rashid',
      email: 'mohammed.ar@example.com',
      tier: 'Pro',
      engagement: 96,
      transactions: 523,
      totalSpend: 18932,
      lastActive: '1 hour ago'
    },
    {
      id: 9,
      name: 'Isabella Santos',
      email: 'isabella.s@example.com',
      tier: 'Free',
      engagement: 48,
      transactions: 43,
      totalSpend: 1234,
      lastActive: '3 days ago'
    },
    {
      id: 10,
      name: 'Alex Novak',
      email: 'alex.n@example.com',
      tier: 'Pro',
      engagement: 85,
      transactions: 289,
      totalSpend: 10567,
      lastActive: '4 hours ago'
    },
    {
      id: 11,
      name: 'Priya Sharma',
      email: 'priya.s@example.com',
      tier: 'Free',
      engagement: 62,
      transactions: 98,
      totalSpend: 2789,
      lastActive: '1 day ago'
    },
    {
      id: 12,
      name: 'Lucas Müller',
      email: 'lucas.m@example.com',
      tier: 'Pro',
      engagement: 89,
      transactions: 356,
      totalSpend: 13245,
      lastActive: '2 hours ago'
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters applied:', newFilters);
  };

  const handleResetFilters = (defaultFilters) => {
    setFilters(defaultFilters);
    console.log('Filters reset');
  };

  const tabs = [
    { id: 'funnel', label: 'User Journey', icon: 'GitBranch' },
    { id: 'heatmap', label: 'Feature Usage', icon: 'Grid' },
    { id: 'spending', label: 'Spending Patterns', icon: 'DollarSign' }
  ];

  return (
    <MainLayout showControls={true}>
      <div className="max-w-[1400px] mx-auto space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              User Analytics Hub
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Comprehensive insights into user behavior, feature adoption, and conversion optimization
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export Report
            </Button>
            <Button variant="default" iconName="RefreshCw" iconPosition="left">
              Refresh Data
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          <div className="lg:col-span-3">
            <FilterPanel
              onApplyFilters={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          <div className="lg:col-span-9">
            <div className="card">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-thin">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </div>

                {activeTab === 'spending' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setChartType('bar')}
                      className={`p-2 rounded-lg transition-smooth ${
                        chartType === 'bar' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:bg-muted/50'
                      }`}
                      aria-label="Bar chart"
                    >
                      <Icon name="BarChart3" size={18} />
                    </button>
                    <button
                      onClick={() => setChartType('line')}
                      className={`p-2 rounded-lg transition-smooth ${
                        chartType === 'line' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:bg-muted/50'
                      }`}
                      aria-label="Line chart"
                    >
                      <Icon name="LineChart" size={18} />
                    </button>
                  </div>
                )}
              </div>

              <div className="min-h-[400px]">
                {activeTab === 'funnel' && (
                  <UserJourneyFunnel
                    data={funnelData}
                    selectedSegment={filters?.segment}
                  />
                )}
                {activeTab === 'heatmap' && (
                  <FeatureHeatmap data={heatmapData} />
                )}
                {activeTab === 'spending' && (
                  <SpendingPatternChart
                    data={spendingData}
                    chartType={chartType}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          <div className="lg:col-span-8">
            <UserBehaviorTable users={userData} />
          </div>

          <div className="lg:col-span-4 space-y-4 md:space-y-6">
            <TopFeaturesRanking features={topFeatures} />
            <SentimentAnalysis data={sentimentData} />
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default UserAnalyticsHub;